import React, { useEffect, useMemo, useState } from 'react';
import { Clock3, Gamepad2, Layers3, RefreshCw } from 'lucide-react';
import { api } from '../../api';
import SearchableSelect from '../common/SearchableSelect';

const formatDuration = (durationMs = 0) => {
  const minutes = Math.floor(Number(durationMs) / 60000);
  const seconds = Math.floor((Number(durationMs) % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
};

export default function GComprisProgressPanel({ coachId }) {
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState('');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!coachId) return;
    api.get(`/api/v2/lms/coach/${coachId}/students`)
      .then(({ data }) => {
        const nextStudents = Array.isArray(data?.data) ? data.data : [];
        setStudents(nextStudents);
        setStudentId((current) => current || String(nextStudents[0]?._id || ''));
      })
      .catch(() => setError('Unable to load assigned students.'));
  }, [coachId]);

  const loadProgress = async () => {
    if (!studentId) {
      setSummary(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(`/api/gcompris/summary/${studentId}`);
      setSummary(data);
    } catch (requestError) {
      setSummary(null);
      setError(requestError.response?.data?.message || 'Unable to load GCompris progress.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProgress();
    // loadProgress intentionally follows the selected student only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const selectedStudent = useMemo(
    () => students.find((student) => String(student._id) === studentId),
    [studentId, students]
  );

  return (
    <section className="mx-auto mt-8 max-w-7xl rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-black uppercase tracking-wide text-indigo-700">
            <Gamepad2 size={15} /> GCompris progress
          </div>
          <h2 className="text-2xl font-black text-slate-950">Student game activity</h2>
          <p className="mt-1 text-sm text-slate-600">Real levels, completions, sessions, and play time reported by GCompris.</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <SearchableSelect
            searchPlaceholder="Search student..."
            value={studentId}
            onChange={(event) => setStudentId(event.target.value)}
            className="min-w-64 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-900"
          >
            {students.length === 0 && <option value="">No assigned students</option>}
            {students.map((student) => (
              <option key={student._id} value={student._id}>{student.name || student.userId || student.email}</option>
            ))}
          </SearchableSelect>
          <button
            type="button"
            onClick={loadProgress}
            disabled={!studentId || loading}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-black text-white disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="mt-5 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</div>}

      {summary && (
        <>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ['Activities', summary.totals?.activities || 0, Gamepad2],
              ['Sessions', summary.totals?.sessions || 0, Layers3],
              ['Levels completed', summary.totals?.completedLevels || 0, Layers3],
              ['Total play time', formatDuration(summary.totals?.durationMs), Clock3],
            ].map(([label, value, Icon]) => (
              <div key={label} className="rounded-2xl bg-slate-50 p-4">
                <Icon size={19} className="mb-2 text-indigo-600" />
                <div className="text-2xl font-black text-slate-950">{value}</div>
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</div>
              </div>
            ))}
          </div>

          <div className="mt-6 overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="text-xs font-black uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="border-b px-3 py-3">Activity</th>
                  <th className="border-b px-3 py-3">Assignment</th>
                  <th className="border-b px-3 py-3">Highest level</th>
                  <th className="border-b px-3 py-3">Completed levels</th>
                  <th className="border-b px-3 py-3">Sessions</th>
                  <th className="border-b px-3 py-3">Play time</th>
                  <th className="border-b px-3 py-3">Last played</th>
                </tr>
              </thead>
              <tbody>
                {(summary.activities || []).map((activity) => (
                  <tr key={`${activity.assignmentId || 'none'}-${activity.activityName}`}>
                    <td className="border-b border-slate-100 px-3 py-3 font-bold text-slate-900">{activity.activityTitle}</td>
                    <td className="border-b border-slate-100 px-3 py-3 text-slate-600">{activity.assignmentId || 'Unassigned'}</td>
                    <td className="border-b border-slate-100 px-3 py-3">Level {activity.highestLevel}</td>
                    <td className="border-b border-slate-100 px-3 py-3">{activity.completedLevels?.join(', ') || '-'}</td>
                    <td className="border-b border-slate-100 px-3 py-3">{activity.sessionCount}</td>
                    <td className="border-b border-slate-100 px-3 py-3">{formatDuration(activity.durationMs)}</td>
                    <td className="border-b border-slate-100 px-3 py-3 text-slate-500">
                      {activity.lastPlayedAt ? new Date(activity.lastPlayedAt).toLocaleString('en-IN') : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && summary.activities?.length === 0 && (
            <div className="mt-6 rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
              No GCompris events have been reported for {selectedStudent?.name || 'this student'} yet.
            </div>
          )}
        </>
      )}
    </section>
  );
}
